import styles from './styles.module.css';
import { useDropzone } from 'react-dropzone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Node } from '../../components/Tree/TreeNode';
import { Button, Icon } from '../../components';
import { Icons } from '../../components/Icon/Icon';
import WorkflowsTable from './components/WorkflowsTable';
import { StorageType } from '../Storage/StorageForm/StorageForm';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu/DropdownMenu';
import { LinkedButton } from '../../components/Button';
import axios from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import CloseButtonRounded from '../../components/CloseButtonRounded/CloseButtonRounded';
import { createPortal } from 'react-dom';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';
import AsideMenuItem from '../../layouts/components/Aside/components/AsideMenuItem';
import AsideMenu from '../../layouts/components/Aside/components/AsideMenu';

const formatLabel = (key: string): string => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper to get country name from domicile object
const getCountryName = (domicile: any): string | null => {
    if (!domicile || typeof domicile !== 'object') return null;
    return domicile.label || domicile.domicileName || domicile.name || null;
};

// Helper to find nested array property in an object
const findNestedArrayProperty = (obj: any): string | null => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return null;
    for (const key in obj) {
        if (Array.isArray(obj[key])) {
            return key;
        }
    }
    return null;
};

// Transform arrays grouped by country into separate independent arrays
const transformCountryGroupedArrays = (data: any): any => {
    if (typeof data !== 'object' || data === null) return data;

    if (Array.isArray(data)) {
        // Check if this array has items with domicile and nested arrays
        const hasCountryGrouping =
            data.length > 0 &&
            data.every(
                (item: any) =>
                    typeof item === 'object' &&
                    item !== null &&
                    !Array.isArray(item) &&
                    item.domicile &&
                    findNestedArrayProperty(item) !== null,
            );

        if (hasCountryGrouping) {
            // This is a country-grouped array, but we'll handle it at the parent level
            // Return as-is for now, parent will handle transformation
            return data;
        }

        // Regular array, recursively transform items
        return data.map((item: any) => transformCountryGroupedArrays(item));
    }

    // Object - recursively transform and check for country-grouped arrays
    const transformed: any = {};

    for (const key in data) {
        if (!data.hasOwnProperty(key)) continue;

        const value = data[key];

        // Check if this is an array that should be split by country
        if (Array.isArray(value) && value.length > 0) {
            const firstItem = value[0];
            if (
                typeof firstItem === 'object' &&
                firstItem !== null &&
                !Array.isArray(firstItem) &&
                firstItem.domicile
            ) {
                const nestedArrayKey = findNestedArrayProperty(firstItem);
                if (nestedArrayKey) {
                    // This array should be flattened - all nested items go into one array
                    const flattenedArray: any[] = [];

                    value.forEach((item: any) => {
                        const countryName = getCountryName(item.domicile);
                        const nestedArray = item[nestedArrayKey];

                        if (countryName && Array.isArray(nestedArray)) {
                            // For each item in the nested array, create a new item with country + nested item properties
                            nestedArray.forEach((nestedItem: any) => {
                                const newItem: any = {
                                    country: countryName, // Just the country name as a string
                                    ...nestedItem, // All properties from the nested item (id, label, value, etc.)
                                };

                                // Also keep other properties from the parent item (excluding domicile and the nested array)
                                for (const propKey in item) {
                                    if (propKey !== 'domicile' && propKey !== nestedArrayKey) {
                                        newItem[propKey] = transformCountryGroupedArrays(item[propKey]);
                                    }
                                }

                                flattenedArray.push(transformCountryGroupedArrays(newItem));
                            });
                        }
                    });

                    // Replace the original array with the flattened one
                    transformed[key] = flattenedArray;
                    continue;
                }
            }
        }

        // Regular property, recursively transform
        transformed[key] = transformCountryGroupedArrays(value);
    }

    return transformed;
};

// Convert snake_case to camelCase
const toCamelCase = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Recursively convert all keys in an object from snake_case to camelCase
const convertKeysToCamelCase = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map((item) => convertKeysToCamelCase(item));
    }

    if (typeof obj !== 'object') {
        return obj;
    }

    const converted: any = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const camelKey = toCamelCase(key);
            converted[camelKey] = convertKeysToCamelCase(obj[key]);
        }
    }
    return converted;
};

// Group indexed items into arrays (e.g., investor_1, investor_2 -> investors[])
const groupIndexedItems = (data: any): any => {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        return data;
    }

    const result: any = {};
    const indexedGroups: Record<string, any[]> = {};

    // Patterns to match: investor_1, asset_1, investment_manager_1, service_provider_1, licence_1, law_1
    const patterns = [
        { prefix: 'investor_', groupKey: 'investors' },
        { prefix: 'asset_', groupKey: 'assets' },
        { prefix: 'investment_manager_', groupKey: 'investmentManagers' },
        { prefix: 'service_provider_', groupKey: 'serviceProviders' },
        { prefix: 'licence_', groupKey: 'licences' },
        { prefix: 'law_', groupKey: 'laws' },
    ];

    // First pass: collect indexed items
    for (const key in data) {
        if (!data.hasOwnProperty(key)) continue;

        let matched = false;
        for (const pattern of patterns) {
            if (key.startsWith(pattern.prefix)) {
                if (!indexedGroups[pattern.groupKey]) {
                    indexedGroups[pattern.groupKey] = [];
                }
                indexedGroups[pattern.groupKey].push({
                    originalKey: key,
                    value: data[key],
                });
                matched = true;
                break;
            }
        }

        if (!matched) {
            result[key] = data[key];
        }
    }

    // Second pass: sort and add grouped items, and recursively process nested objects
    for (const key in result) {
        if (!result.hasOwnProperty(key)) continue;
        result[key] = groupIndexedItems(result[key]);
    }

    // Add grouped arrays (sorted by original key)
    for (const [groupKey, items] of Object.entries(indexedGroups)) {
        items.sort((a, b) => {
            const aNum = parseInt(a.originalKey.match(/\d+$/)?.[0] || '0');
            const bNum = parseInt(b.originalKey.match(/\d+$/)?.[0] || '0');
            return aNum - bNum;
        });
        result[groupKey] = items.map((item) => groupIndexedItems(item.value));
    }

    return result;
};

// Per-file input: fileName + parsed data (e.g. JSON). Parsed data is combined from all files.
export type FileInputItem = { fileName: string; data: Record<string, any> };

const combineFileInputs = (fileInputs: FileInputItem[]): Record<string, any> => {
    const combined: Record<string, any> = {};
    for (const { data } of fileInputs) {
        for (const key in data) {
            if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
            const val = data[key];
            if (Array.isArray(val)) {
                combined[key] = [...(Array.isArray(combined[key]) ? combined[key] : []), ...val];
            } else if (typeof val === 'object' && val !== null) {
                combined[key] = {
                    ...(typeof combined[key] === 'object' && combined[key] !== null && !Array.isArray(combined[key])
                        ? combined[key]
                        : {}),
                    ...val,
                };
            } else {
                combined[key] = val;
            }
        }
    }
    return combined;
};

// Map transformed JSON data to StorageType structure
const mapToStorageTypeStructure = (data: any): Record<string, any> => {
    // Convert all keys to camelCase without any grouping transformations
    const camelCased = convertKeysToCamelCase(data);

    // IMPORTANT:
    // - Do NOT pre-create StorageType keys (optional objects must not appear if absent in JSON)
    // - Support both "already StorageType-shaped" payloads (Structure/Investors/...) and legacy keys (vehicle/gp/etc.)
    const result: Record<string, any> = {};

    const normalizeKey = (key: string): string => key.replace(/[\s_-]/g, '').toLowerCase();
    const normalizeToStorageType = (key: string): StorageType | null => {
        const normalized = normalizeKey(key);
        const storageTypeValues = Object.values(StorageType) as string[];
        const match = storageTypeValues.find((v) => normalizeKey(v) === normalized);
        return (match as StorageType) || null;
    };

    const setIfPresent = (storageType: StorageType, value: any) => {
        if (value === undefined) return;
        result[storageType] = value;
    };

    // If data is valid, map keys to StorageType structure
    if (typeof camelCased === 'object' && camelCased !== null && !Array.isArray(camelCased)) {
        // 1) If payload already contains StorageType keys (Structure/Investors/...), normalize them to enum keys.
        const mappedInputKeys = new Set<string>();
        for (const key in camelCased) {
            if (!camelCased.hasOwnProperty(key)) continue;
            const storageTypeKey = normalizeToStorageType(key);
            if (storageTypeKey) {
                setIfPresent(storageTypeKey, camelCased[key]);
                mappedInputKeys.add(key);
            }
        }

        // 2) Legacy/alternate key mapping (only if not already present via direct StorageType keys).
        if (result[StorageType.Structure] === undefined) {
            setIfPresent(StorageType.Structure, camelCased.vehicle ?? camelCased.structure);
        }
        if (result[StorageType.GeneralPartner] === undefined) {
            setIfPresent(StorageType.GeneralPartner, camelCased.gp ?? camelCased.generalPartner);
        }
        if (result[StorageType.Investors] === undefined) {
            setIfPresent(StorageType.Investors, camelCased.investors);
        }
        if (result[StorageType.Assets] === undefined) {
            setIfPresent(StorageType.Assets, camelCased.assets);
        }
        if (result[StorageType.AssetManager] === undefined) {
            setIfPresent(StorageType.AssetManager, camelCased.investmentManagers ?? camelCased.assetManager);
        }
        if (result[StorageType.ServiceProviders] === undefined) {
            setIfPresent(StorageType.ServiceProviders, camelCased.serviceProviders);
        }
        if (result[StorageType.Other] === undefined) {
            setIfPresent(StorageType.Other, camelCased.other);
        }
        if (result[StorageType.OtherPartTwo] === undefined) {
            setIfPresent(StorageType.OtherPartTwo, camelCased.otherPartTwo);
        }

        // 3) Include any other top-level keys that don't need mapping (but avoid duplicating legacy keys)
        const excludedLegacyKeys = new Set([
            'vehicle',
            'structure',
            'gp',
            'generalPartner',
            'investors',
            'assets',
            'investmentManagers',
            'assetManager',
            'serviceProviders',
            'other',
            'otherPartTwo',
        ]);

        for (const key in camelCased) {
            if (!camelCased.hasOwnProperty(key)) continue;
            if (mappedInputKeys.has(key)) continue;
            if (excludedLegacyKeys.has(key)) continue;
            result[key] = camelCased[key];
        }
    }

    return result;
};

// convert example json data to tree data (test)
const recursiveConverter = (data: any, parentValuePrefix: string = ''): Node[] => {
    const nodes: Node[] = [];
    for (const key in data) {
        if (!data.hasOwnProperty(key)) continue;
        const item = data[key];
        const nodeValue = parentValuePrefix + key.toLowerCase();
        const nodeLabel = formatLabel(key);
        const node: Node = {
            value: nodeValue,
            label: nodeLabel,
        };
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            node.children = recursiveConverter(item, `${nodeValue}-`);
        } else if (Array.isArray(item)) {
            node.children = item.map((arrItem, arrIndex) => ({
                value: `${nodeValue}-element-${arrIndex}`,
                label: String(arrItem) || `[Empty Value ${arrIndex}]`,
            }));
        }
        nodes.push(node);
    }
    return nodes;
};

// Display: group only evergreen and openEnded under "Liquidity terms"; keep array fields as-is; skip notes
const LIQUIDITY_TERMS_KEYS = ['evergreen', 'openEnded'];

const transformOtherForTable = (data: Record<string, any> | null): Record<string, any> | null => {
    if (!data) return data;
    const other = data.Other ?? data.other;
    if (!other || typeof other !== 'object') return data;
    const key = data.Other !== undefined ? 'Other' : 'other';
    const liquidityTerms: Record<string, any> = {};
    const rest: Record<string, any> = {};
    for (const k of Object.keys(other)) {
        if (k === 'notes') continue;
        if (LIQUIDITY_TERMS_KEYS.includes(k)) {
            liquidityTerms[k] = other[k];
        } else {
            rest[k] = other[k];
        }
    }
    return { ...data, [key]: { 'Liquidity terms': liquidityTerms, licenses: [], localTaxStatus: [], ...rest } };
};

// Flatten display shape back to backend shape: Liquidity terms → top-level evergreen/openEnded; preserve notes
const flattenOtherFromTable = (data: Record<string, any>, currentNotes?: string): Record<string, any> => {
    if (!data) return data;
    const other = data.Other ?? data.other;
    if (!other || typeof other !== 'object') return data;
    const key = data.Other !== undefined ? 'Other' : 'other';
    const liquidity = other['Liquidity terms'];
    const flatOther: Record<string, any> = {};
    if (liquidity && typeof liquidity === 'object') {
        flatOther.evergreen = liquidity.evergreen;
        flatOther.openEnded = liquidity.openEnded;
    }
    for (const k of Object.keys(other)) {
        if (k === 'Liquidity terms') continue;
        flatOther[k] = other[k];
    }
    flatOther.notes = currentNotes ?? other.notes ?? '';
    return { ...data, [key]: flatOther };
};

type ActionTaskOption = {
    id: string;
    label: string;
    icon: Icons;
    disabled?: boolean;
};

const informationSourcesOptions: ActionTaskOption[] = [
    {
        id: 'legal_knowledge',
        label: 'Structured legal knowledge',
        icon: Icons.info,
        disabled: false,
    },
    {
        id: 'global_web',
        label: 'Global web',
        icon: Icons.info,
        disabled: false,
    },
    {
        id: 'your_vault',
        label: 'Your vault',
        icon: Icons.info,
        disabled: false,
    },
    {
        id: 'legal_sources',
        label: 'Global legal sources',
        icon: Icons.info,
        disabled: false,
    },
];

const tasksOptions: ActionTaskOption[] = [
    {
        id: 'extract_structure',
        label: 'Extract structure features',
        icon: Icons.task,
    },
    {
        id: 'compliance_checks',
        label: 'Run compliance checks',
        icon: Icons.task,
        disabled: true,
    },
    {
        id: 'tax_reviews',
        label: 'Tax reviews',
        icon: Icons.task,
    },
    {
        id: 'risk_factors_review',
        label: 'Risk factors review',
        icon: Icons.task,
        disabled: true,
    },
    {
        id: 'qa',
        label: "Q&A's",
        icon: Icons.task,
        disabled: true,
    },
];

const knowledgeBaseOptions: ActionTaskOption[] = [
    {
        id: 'nodes',
        label: 'Nodes',
        icon: Icons.knowledgeBase,
        disabled: true,
    },
    {
        id: 'rag',
        label: 'RAG',
        icon: Icons.knowledgeBase,
        disabled: true,
    },

    {
        id: 'kbs_qa',
        label: "Q&A's",
        icon: Icons.knowledgeBase,
        disabled: true,
    },
];

const WorkflowsActions = ({
    actions,
    handleActionChange,
    selectedActions = [],
    showSelected = true,
    disabled = false,
}: {
    actions: string[];
    handleActionChange: any;
    selectedActions?: string[];
    showSelected?: boolean;
    disabled?: boolean;
}) => {
    const allActions = [
        {
            id: 'global_legal_sources',
            label: 'Global legal sources',
            icon: Icons.info,
        },
        {
            id: 'legal_knowledge',
            label: 'Structured legal knowledge',
            icon: Icons.info,
        },
        ...informationSourcesOptions,
        ...tasksOptions,
        ...knowledgeBaseOptions,
    ];

    return (
        <>
            {actions.map((id, i) => {
                const action = allActions.find((a) => a.id === id);
                const isSelected = selectedActions.includes(id);

                if (!action || (!showSelected && isSelected)) return null;

                const Tag = isSelected ? 'div' : 'label';

                return (
                    <Tag
                        key={i}
                        className={`${styles.action} ${isSelected ? styles.selectedAction : ''} ${disabled ? styles.disabled : ''}`}
                    >
                        <input
                            type="checkbox"
                            id={'action_checkbox_' + id}
                            name={'actions[]'}
                            value={id}
                            checked={isSelected}
                            onChange={handleActionChange}
                            disabled={disabled}
                        />
                        {!!action.icon ? <Icon icon={action.icon} /> : null}

                        <span className={styles.actionLabel}>{action.label}</span>
                        {isSelected ? (
                            <Icon icon={Icons.check} />
                        ) : (
                            <Icon icon={Icons.plus} className={styles.actionPlusIcon} />
                        )}

                        {isSelected && (
                            <label htmlFor={'action_checkbox_' + id} className={styles.closeWrapper}>
                                <CloseButtonRounded el={'span'} />
                            </label>
                        )}
                    </Tag>
                );
            })}
        </>
    );
};

type AiModelOption = {
    id: string;
    label: string;
    subTitle: string;
    icon: Icons;
};

const aiModelOptions: AiModelOption[] = [
    {
        id: 'gpt5.2',
        label: 'GPT 5.2 with White Thesis',
        subTitle: 'Latest OpenAI model',
        icon: Icons.chatGpt,
    },
    {
        id: 'gpt5.0',
        label: 'GPT 5.0 with White Thesis',
        subTitle: 'Fastest OpenAI model',
        icon: Icons.chatGpt,
    },
    {
        id: 'claude4.5',
        label: 'Claude Sonnet 4.5 with White Thesis',
        subTitle: 'Latest Anthropic model',
        icon: Icons.claudeIcon,
    },
    {
        id: 'gemini3.0',
        label: 'Gemini 3 with White Thesis',
        subTitle: 'Latest Google model',
        icon: Icons.geminiIcon,
    },
];

const WorkflowsUpload = () => {
    const [fileInputs, setFileInputs] = useState<FileInputItem[]>([]);
    const [parsedData, setParsedData] = useState<Record<string, any> | null>();
    const [selectedData, setSelectedData] = useState<Record<string, any>>();
    const [actions, setActions] = useState<string[]>([]);
    const [error, setError] = useState<boolean>(false);
    const [showStructureForm, setShowStructureForm] = useState<boolean>(false);
    const [selectedAiModel, setSelectedAiModel] = useState<AiModelOption | null>(aiModelOptions[0]);
    const [selectedInformationSources, setSelectedInformationSources] = useState<string[]>([]);

    const [isMenuFocused, setIsMenuFocused] = useState<boolean>(false);
    const [isAIMenuFocused, setIsAIMenuFocused] = useState<boolean>(false);
    const previewAbortRef = useRef<AbortController | null>(null);

    const navigate = useNavigate();

    const files = fileInputs.map((f) => f.fileName);

    const [selectedStructureActions, setSelectedStructureActions] = useState<string[]>(['structure']);

    useEffect(() => {
        if (!actions.length && parsedData) {
            setShowStructureForm(false);
            setParsedData(null);
        }
    }, [actions]);

    useEffect(() => {
        dispatchAsidePortalContentChanged(showStructureForm);
    }, [showStructureForm]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setError(false);
        acceptedFiles.forEach((file) => {
            const reader = new FileReader();
            reader.onabort = () => console.log('file reading was aborted');
            reader.onerror = () => console.log('file reading has failed');
            reader.onload = () => {
                const content = reader.result;
                if (typeof content !== 'string') return;
                try {
                    const data = file.name.toLowerCase().endsWith('.json') ? JSON.parse(content) : {};
                    setFileInputs((prev) => [...prev, { fileName: file.name, data }]);
                } catch (e) {
                    console.log(e);
                    setError(true);
                }
            };
            reader.readAsText(file);
        });
    }, []);
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'application/json': ['.json'],
            'application/pdf': ['.pdf'],
        },
    });

    const handleStructureActionChange = (e: any) => {
        const value = e.target.value;
        if (!selectedStructureActions.includes(value)) {
            setSelectedStructureActions([...selectedStructureActions, value]);
        } else {
            setSelectedStructureActions(selectedStructureActions.filter((a) => a !== value));
        }
    };

    const handleActionChangeDirect = (value: string) => {
        if (!actions.includes(value)) {
            setActions([...actions, value]);
        } else {
            setActions(actions.filter((a) => a !== value));
        }
    };

    const handleActionChange = (e: any) => {
        handleActionChangeDirect(e.target.value);
    };

    const parseFromFileInputs = useCallback(() => {
        if (fileInputs.length === 0) {
            setParsedData(null);
            setSelectedData(undefined);
            return;
        }
        const combined = combineFileInputs(fileInputs);
        setParsedData(mapToStorageTypeStructure(combined));
    }, [fileInputs]);

    const runTask = () => {
        parseFromFileInputs();
    };

    // Helper function to recursively remove null values from objects and arrays
    const removeNullValues = (obj: any): any => {
        if (obj === null || obj === undefined) {
            return undefined;
        }

        if (Array.isArray(obj)) {
            // Filter out null values and recursively clean remaining items
            return obj.map((item) => removeNullValues(item)).filter((item) => item !== null && item !== undefined);
        }

        if (typeof obj === 'object') {
            const cleaned: any = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const value = removeNullValues(obj[key]);
                    // Only include the key if the value is not null/undefined
                    if (value !== null && value !== undefined) {
                        cleaned[key] = value;
                    }
                }
            }
            return cleaned;
        }

        return obj;
    };

    const getPreview = (mode: string) => async (e: any) => {
        e.preventDefault();
        try {
            const cleanedData = removeNullValues(selectedData);

            if (mode === 'storage') {
                sessionStorage.setItem('storagePreview', JSON.stringify(cleanedData));
                navigate(`/storage/preview`);
            } else {
                previewAbortRef.current?.abort();
                const controller = new AbortController();
                previewAbortRef.current = controller;
                const res = await axios.post(`/workflows/preview?mode=${mode}`, cleanedData, {
                    signal: controller.signal,
                });
                console.log(res);

                if (!res.data) return;

                // @ts-ignore
                const params = new URLSearchParams(
                    // @ts-ignore
                    Object.entries(res.data).map((entry) => {
                        // @ts-ignore
                        entry[1] = JSON.stringify(entry[1]);
                        return entry;
                    }),
                );

                navigate(`/deal-taxes?${params}&preview=true`);
            }
        } catch (e) {
            const isCancelled =
                (e as Error)?.name === 'AbortError' || (e as { code?: string })?.code === 'ERR_CANCELED';
            if (isCancelled) return;
            console.log(e);
        }
    };

    const removeFile = (index: number) => {
        setFileInputs((prev) => {
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) {
                setParsedData(null);
                setSelectedData(undefined);
            } else {
                const combined = combineFileInputs(next);
                setParsedData(mapToStorageTypeStructure(combined));
            }
            return next;
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {parsedData ? (
                    <>
                        <div className={`${styles.parsedSection} ${styles.rightColumn}`}>
                            <section
                                className={`${styles.dropzoneContainer}  ${files.length > 0 ? styles.active : ''}`}
                            >
                                {files.length > 0 && (
                                    <div className={styles.fileList}>
                                        {files.map((item, index) => (
                                            <div key={index} className={styles.fileItem}>
                                                <div className={styles.fileTypeContainer}>
                                                    <Icon icon={Icons.file} />

                                                    <span className={styles.fileType}>{item.split('.').pop()}</span>
                                                </div>

                                                <div className={styles.fileItemInfo}>
                                                    <span className={styles.fileItemName}>{item}</span>
                                                    <span className={styles.fileItemSubtitle}>47 lines</span>
                                                </div>

                                                <div className={styles.closeWrapper}>
                                                    <CloseButtonRounded onClick={() => removeFile(index)} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {!!actions.length && (
                                    <div className={`${styles.dropzoneActions} ${styles.dropzoneActionsRight}`}>
                                        <WorkflowsActions
                                            actions={actions}
                                            selectedActions={actions}
                                            handleActionChange={handleActionChange}
                                            // disabled={true}
                                        />
                                    </div>
                                )}
                                {error && <p className={styles.errorMessage}>No data found</p>}
                                <button
                                    onClick={() => {
                                        setParsedData(null);
                                        setShowStructureForm(false);
                                    }}
                                    className={styles.actionBtn}
                                >
                                    <Icon icon={showStructureForm ? Icons.return : Icons.penOutline} />
                                </button>
                            </section>

                            <section className={`${styles.tableContainer}`}>
                                <button
                                    onClick={() => {
                                        setParsedData(null);
                                        setShowStructureForm(false);
                                    }}
                                    className={`${styles.actionBtn} ${styles.active}`}
                                >
                                    <Icon icon={Icons.close} />
                                </button>

                                {showStructureForm ? (
                                    <div className={`${styles.tableContainerPreview}`}>
                                        {document.querySelector('#asideTitle') &&
                                            createPortal('New Fund LLP', document.querySelector('#asideTitle')!)}
                                        {document.querySelector('#menuAsideContent') &&
                                            createPortal(
                                                <AsideMenu open={true} isStandalone={true}>
                                                    {selectedStructureActions.includes('tax_review') && (
                                                        <AsideMenuItem onClick={getPreview('tax_review')}>
                                                            Deal taxes
                                                        </AsideMenuItem>
                                                    )}
                                                    {selectedStructureActions.includes('structure') && (
                                                        <AsideMenuItem onClick={getPreview('storage')}>
                                                            Store structure
                                                        </AsideMenuItem>
                                                    )}
                                                    {selectedStructureActions.includes('compliance_review') && (
                                                        <AsideMenuItem to={'/deal-taxes'}>
                                                            Compliance review
                                                        </AsideMenuItem>
                                                    )}
                                                </AsideMenu>,
                                                document.querySelector('#menuAsideContent')!,
                                            )}

                                        <h2 className={styles.title}>
                                            <img src="/logo-small.svg" alt="" />
                                            Extracted structure features:
                                        </h2>

                                        <div className={styles.selectedFormPreviewActions}>
                                            {selectedStructureActions.includes('tax_review') && (
                                                <div>
                                                    <div className={styles.selectedPreviewBox}>
                                                        <img src="./images/thumb-taxes.png" alt="" />
                                                        Deal taxes
                                                    </div>

                                                    <Button
                                                        size={'sm'}
                                                        color={'neutral'}
                                                        after={<Icon icon={Icons.link} />}
                                                        onClick={getPreview('tax_review')}
                                                    >
                                                        Detailed view
                                                    </Button>
                                                </div>
                                            )}

                                            {selectedStructureActions.includes('structure') && (
                                                <div>
                                                    <div className={styles.selectedPreviewBox}>
                                                        <img src="./images/thumb-storage.png" alt="" />
                                                        Store structure
                                                    </div>

                                                    <Button
                                                        size={'sm'}
                                                        color={'neutral'}
                                                        after={<Icon icon={Icons.link} />}
                                                        onClick={getPreview('storage')}
                                                    >
                                                        Store structure
                                                    </Button>
                                                </div>
                                            )}

                                            {selectedStructureActions.includes('compliance_review') && (
                                                <div>
                                                    <div className={styles.selectedPreviewBox}>
                                                        <img src="./images/thumb-compliance.png" alt="" />
                                                        Compliance review
                                                    </div>

                                                    <LinkedButton
                                                        to={'/deal-taxes'}
                                                        size={'sm'}
                                                        color={'neutral'}
                                                        after={<Icon icon={Icons.link} />}
                                                    >
                                                        Detailed view
                                                    </LinkedButton>
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles.selectedFormActions}>
                                            <Button
                                                size={'sm'}
                                                outlined={true}
                                                onClick={() => {
                                                    setShowStructureForm(false);
                                                }}
                                                hasMinWidth={false}
                                            >
                                                Edit
                                            </Button>
                                            <Button size={'sm'} color={'neutral'}>
                                                Save all
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <WorkflowsTable
                                        data={transformOtherForTable(parsedData) ?? undefined}
                                        selectedActions={selectedStructureActions}
                                        handleActionChange={handleStructureActionChange}
                                        onSelectionChange={(dataWithSelection) => {
                                            setParsedData((prev) =>
                                                flattenOtherFromTable(
                                                    dataWithSelection,
                                                    prev?.Other?.notes ?? prev?.other?.notes,
                                                ),
                                            );
                                        }}
                                        onRunTask={(dataWithSelection) => {
                                            setSelectedData(
                                                flattenOtherFromTable(
                                                    dataWithSelection,
                                                    parsedData?.Other?.notes ?? parsedData?.other?.notes,
                                                ),
                                            );
                                            setShowStructureForm(true);
                                        }}
                                    />
                                )}
                            </section>
                        </div>

                        {/*{showStructureForm && (
                            <section
                                className={`${styles.mainContainer} ${styles.tableContainer} ${styles.rightColumn}`}
                            >
                                <WorkflowsStructureForm data={parsedData} selectedPaths={selectedKeys} />
                            </section>
                        )}*/}
                    </>
                ) : (
                    <section>
                        <div
                            className={`${styles.dropzoneContainer} ${styles.dropzoneContainerWide} ${files.length > 0 ? styles.active : ''}`}
                        >
                            {files.length > 0 && (
                                <div className={styles.fileList}>
                                    {files.map((item, index) => (
                                        <div key={index} className={styles.fileItem}>
                                            <div className={styles.fileTypeContainer}>
                                                <Icon icon={Icons.file} />

                                                <span className={styles.fileType}>{item.split('.').pop()}</span>
                                            </div>

                                            <div className={styles.fileItemInfo}>
                                                <span className={styles.fileItemName}>{item}</span>
                                                <span className={styles.fileItemSubtitle}>47 lines</span>
                                            </div>

                                            <div className={styles.closeWrapper}>
                                                <CloseButtonRounded onClick={() => removeFile(index)} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!!actions.length && (
                                <div className={`${styles.dropzoneActions} ${styles.margin}`}>
                                    <WorkflowsActions
                                        actions={actions}
                                        selectedActions={actions}
                                        handleActionChange={handleActionChange}
                                    />
                                </div>
                            )}

                            {error && <p className={styles.errorMessage}>No data found</p>}
                            <h2>Upload your files, select task or write a request …</h2>
                            <div className={styles.dropzone} {...getRootProps()}>
                                <input {...getInputProps()} />
                                <Icon icon={Icons.upload} />
                            </div>
                            <div className={styles.dropzoneActionsContainer}>
                                <div className={styles.dropzoneActions}>
                                    <DropdownMenu
                                        onFocus={() => setIsMenuFocused(true)}
                                        onBlur={() => setIsMenuFocused(false)}
                                        dropdownTrigger={
                                            <button
                                                className={`withInfo ${isMenuFocused ? `withInfoDisabled ${styles.dropDownTriggerOpen}` : ''} ${styles.dropDownTrigger}`}
                                            >
                                                <Icon icon={Icons.plus} />
                                            </button>
                                        }
                                        dropDownMenuClassName={styles.dropdownMenuCustomRight}
                                    >
                                        <DropdownMenuItem
                                            icon={Icons.uploadSimple}
                                            onClick={() => {
                                                // @ts-expect-error types
                                                document.querySelector('input[type="file"]')?.click();
                                            }}
                                        >
                                            Add files
                                        </DropdownMenuItem>
                                        {/*<DropdownMenuItem>*/}
                                        <DropdownMenu
                                            dropdownTrigger={
                                                <DropdownMenuItem
                                                    icon={Icons.info}
                                                    hasChildren={true}
                                                    borderBottom={true}
                                                >
                                                    Information sources
                                                </DropdownMenuItem>
                                            }
                                        >
                                            {informationSourcesOptions.map((option) => {
                                                const isSelected = selectedInformationSources.includes(option.id);
                                                const atMax = selectedInformationSources.length >= 2;
                                                const disabled = option.disabled || (!isSelected && atMax);
                                                return (
                                                    <DropdownMenuItem
                                                        key={option.id}
                                                        icon={option.icon}
                                                        isSelected={isSelected}
                                                        disabled={disabled}
                                                        onClick={() => {
                                                            if (disabled) return;
                                                            setSelectedInformationSources((prev) =>
                                                                prev.includes(option.id)
                                                                    ? prev.filter((id) => id !== option.id)
                                                                    : [...prev, option.id],
                                                            );
                                                            handleActionChangeDirect(option.id);
                                                        }}
                                                        blurOnClick={false}
                                                    >
                                                        {option.label}
                                                    </DropdownMenuItem>
                                                );
                                            })}
                                        </DropdownMenu>

                                        <DropdownMenu
                                            dropdownTrigger={
                                                <DropdownMenuItem icon={Icons.task} hasChildren={true}>
                                                    Tasks
                                                </DropdownMenuItem>
                                            }
                                        >
                                            {tasksOptions.map((option) => (
                                                <DropdownMenuItem
                                                    key={option.id}
                                                    icon={option.icon}
                                                    isSelected={actions.includes(option.id)}
                                                    onClick={() => {
                                                        if (option.disabled) return;
                                                        handleActionChangeDirect(option.id);
                                                    }}
                                                    blurOnClick={false}
                                                >
                                                    {option.label}
                                                </DropdownMenuItem>
                                            ))}
                                            <DropdownMenuItem key={'create'} icon={Icons.plus} borderTop={true}>
                                                Create new
                                            </DropdownMenuItem>
                                        </DropdownMenu>

                                        <DropdownMenu
                                            dropdownTrigger={
                                                <DropdownMenuItem icon={Icons.knowledgeBase} hasChildren={true}>
                                                    Create & add knowledge
                                                </DropdownMenuItem>
                                            }
                                            hasMinWidth={false}
                                        >
                                            {knowledgeBaseOptions.map((option) => (
                                                <DropdownMenuItem
                                                    key={option.id}
                                                    icon={option.icon}
                                                    isSelected={actions.includes(option.id)}
                                                    onClick={() => {
                                                        if (option.disabled) return;
                                                        handleActionChangeDirect(option.id);
                                                    }}
                                                    blurOnClick={false}
                                                >
                                                    {option.label}
                                                </DropdownMenuItem>
                                            ))}
                                            <DropdownMenuItem key={'create'} icon={Icons.plus} borderTop={true}>
                                                Create new
                                            </DropdownMenuItem>
                                        </DropdownMenu>
                                        {/*</DropdownMenuItem>*/}
                                    </DropdownMenu>
                                </div>

                                <div className={styles.dropzoneActions}>
                                    <DropdownMenu
                                        onFocus={() => setIsAIMenuFocused(true)}
                                        onBlur={() => setIsAIMenuFocused(false)}
                                        dropdownTrigger={
                                            <button
                                                className={`${styles.aiDropDownTrigger} ${isAIMenuFocused ? styles.aiDropDownTriggerOpen : ''}`}
                                            >
                                                <Icon icon={selectedAiModel?.icon ?? Icons.chatGpt} />
                                                {selectedAiModel?.label ?? 'Select AI model'}
                                                <Icon icon={Icons.chevronDown} />
                                            </button>
                                        }
                                        dropDownMenuClassName={styles.dropdownMenuCustomWideRight}
                                    >
                                        {aiModelOptions.map((option) => (
                                            <DropdownMenuItem
                                                key={option.id}
                                                icon={option.icon}
                                                iconSize={'md'}
                                                subTitle={option.subTitle}
                                                isSelected={selectedAiModel?.id === option.id}
                                                onClick={() => setSelectedAiModel(option)}
                                            >
                                                {option.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenu>
                                    <Button
                                        disabled={!files?.length || !actions.length}
                                        size={'sm'}
                                        color={'neutral'}
                                        onClick={runTask}
                                    >
                                        Run task
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className={`${styles.dropzoneActions} ${styles.dropzoneActionsOuter}`}>
                            <WorkflowsActions
                                actions={[
                                    'global_legal_sources',
                                    'legal_knowledge',
                                    'extract_structure',
                                    'tax_reviews',
                                ]}
                                handleActionChange={handleActionChange}
                                selectedActions={actions}
                                showSelected={false}
                            />
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default WorkflowsUpload;
